import { translate, localHref } from "../i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { PRODUCTS } from "../content/products";
const categories = [translate("Tutti"), translate("Azienda"), translate("Editoria"), translate("Etichette"), translate("Grande formato"), translate("Ristorazione"), translate("Eventi e regali")];
export default function Products({
  onSelect
}) {
  const [category, setCategory] = useState(translate("Tutti"));
  const [query, setQuery] = useState("");
  const reduced = useReducedMotion();
  const rail = useRef(null);
  const [edges, setEdges] = useState({
    start: true,
    end: false
  });
  const normalize = s => s.toLocaleLowerCase("it").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const visible = PRODUCTS.filter(p => (category === translate("Tutti") || p.category === category) && normalize(p.title + " " + p.description).includes(normalize(query.trim())));
  const measure = useCallback(() => {
    const el = rail.current;
    if (el) setEdges({
      start: el.scrollLeft < 4,
      end: el.scrollWidth - el.clientWidth - el.scrollLeft < 4
    });
  }, []);
  useEffect(() => {
    const el = rail.current;
    if (!el) return;
    el.scrollLeft = 0;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [category, query, measure]);
  const move = direction => {
    const el = rail.current;
    if (!el || !el.children.length) return;
    const cards = [...el.children];
    const origin = cards[0].offsetLeft;
    const positions = cards.map(card => card.offsetLeft - origin);
    const nearest = positions.reduce((best, position, index) => Math.abs(position - el.scrollLeft) < Math.abs(positions[best] - el.scrollLeft) ? index : best, 0);
    const step = positions[1] || el.clientWidth;
    const pageSize = Math.max(1, Math.round(el.clientWidth / step));
    const target = Math.max(0, Math.min(cards.length - 1, nearest + direction * pageSize));
    el.scrollTo({
      left: positions[target],
      behavior: reduced ? "auto" : "smooth"
    });
  };
  return <section id="prodotti" className="ph-products ph-geometry-section">
      <div className="ph-wrap">
        <div className="ph-section-heading">
          <div>
            <p className="ph-label">{translate("IL TUO MARCHIO. MILLE POSSIBILIT\xC0.")}</p>
            <h2>{translate("Idee da guardare.")}<br />
              <span>{translate("Prodotti da toccare.")}</span>
            </h2>
          </div>
          <p>{translate("Sfoglia, scegli, personalizza.")}<br />{translate("Il prossimo progetto comincia qui.")}</p>
        </div>
        <div className="ph-product-filters" role="group" aria-label={translate("Filtra i prodotti")}>
          {categories.map(c => <button key={c} aria-pressed={c === category} aria-controls="product-grid" onClick={() => setCategory(c)}>
              {c}
            </button>)}
        </div>
        <div className="ph-gallery-toolbar">
          <label className="ph-product-search">
            <MagnifyingGlass size={22} />
            <span className="sr-only">{translate("Cerca un prodotto")}</span>
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={translate("Cerca: buste, libri, etichette\u2026")} />
            {query && <button type="button" onClick={() => setQuery("")} aria-label={translate("Cancella ricerca")}>
                <X size={19} />
              </button>}
          </label>
          <div className="ph-gallery-controls">
            <p aria-live="polite">
              {visible.length} {visible.length === 1 ? translate("prodotto") : translate("prodotti")}
            </p>
          </div>
        </div>
        <div className="ph-carousel">
            <button className="ph-carousel-arrow ph-carousel-prev" onClick={() => move(-1)} disabled={edges.start || !visible.length} aria-label={translate("Prodotti precedenti")} aria-controls="product-grid">
              <ArrowLeft size={38} weight="bold" />
            </button>
            <button className="ph-carousel-arrow ph-carousel-next" onClick={() => move(1)} disabled={edges.end || !visible.length} aria-label={translate("Prodotti successivi")} aria-controls="product-grid">
              <ArrowRight size={38} weight="bold" />
            </button>
        <div ref={rail} className="ph-product-grid ph-product-rail" id="product-grid" role="region" aria-label={translate("Galleria prodotti, scorri orizzontalmente")} tabIndex={0} onScroll={measure} onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
            e.preventDefault();
            move(e.key === "ArrowRight" ? 1 : -1);
          }
        }}>
          {visible.map(p => <article key={p.id} className="ph-product">
              <a className="ph-product-photo" href={localHref("/catalogo/" + p.id + "/")} aria-label={translate("Scopri i dettagli di ") + p.title}>
                {p.sheet ? <div className="ph-product-sprite" role="img" aria-label={p.title + translate(" con il logo Pedrazzini \u2014 esempio illustrativo")} style={{
                backgroundImage: `url(/images/products/${p.sheet}-collection.webp)`,
                backgroundPosition: `${p.cell % 3 * 50}% ${Math.floor(p.cell / 3) * 100}%`
              }} /> : <img src={"/images/products/" + p.id + ".webp"} srcSet={`/images/products/${p.id}-600.webp 600w, /images/products/${p.id}.webp 1000w`} sizes="(max-width: 640px) 80vw, (max-width: 1000px) 45vw, 28vw" decoding="async" alt={p.title + translate(" con il logo Pedrazzini \u2014 esempio illustrativo")} width="1000" height="1000" loading="lazy" />}
                <span aria-hidden="true">{translate("Scopri il prodotto")}</span>
              </a>
              <div className="ph-product-copy">
                <p className="ph-label">{p.category}</p>
                <h3>
                  <a href={localHref("/catalogo/" + p.id + "/")}>{p.title}</a>
                </h3>
                <p>{p.description}</p>
                <a className="ph-product-more" aria-label={translate("Scopri materiali e possibilit\xE0: {0}", {
                0: p.title
              })} href={localHref("/catalogo/" + p.id + "/")}>{translate("Scopri materiali e possibilit\xE0 ")}<ArrowRight size={18} />
                </a>
                <button className="ph-link" onClick={() => onSelect(p)}>{translate("Richiedi preventivo ")}<ArrowUpRight size={20} />
                </button>
              </div>
            </article>)}
          {!visible.length && <div className="ph-gallery-empty">
              <h3>{translate("Nessun prodotto trovato.")}</h3>
              <p>{translate("Prova un altro termine oppure esplora tutto il catalogo.")}</p>
              <button className="ph-link" onClick={() => {
              setCategory(translate("Tutti"));
              setQuery("");
            }}>{translate("Mostra tutti i prodotti ")}<ArrowRight size={20} />
              </button>
            </div>}
        </div>
        </div>
        <p className="ph-product-note">{translate("Scorri la galleria o usa le frecce. Esempi illustrativi con il nostro marchio: ogni prodotto si personalizza con la tua identit\xE0. Materiali e lavorazioni vengono definiti nel preventivo.")}</p>
      </div>
    </section>;
}
